import { ConfigService } from '@core/shared/config';
import { getModelId, matchContextGroups } from '@core/shared/model';
import { convertToSubject } from '@core/shared/utils';
import { Store } from '@ngxs/store';
import { map, take } from 'rxjs';
import { AccessState } from '../../access';
import { DevToolsRegistry } from '../../dev-tools';
import { InstructionHelper } from '../../instructions';
import { FeatureService } from '../feature-registry';
import * as i0 from "@angular/core";
import * as i1 from "../../dev-tools";
import * as i2 from "@core/shared/config";
import * as i3 from "../feature-registry";
import * as i4 from "@ngxs/store";
/**
 * model actions can be registered through FeatureDefinitions or by injecting ModelActionService and using the registerModelAction method.
 */
export let ModelFieldInstructionService = /*#__PURE__*/(() => {
  class ModelFieldInstructionService {
    constructor(debug, config, feature, store) {
      this.store = store;
      this.platform = config.getPlatform();
      this.featureBasedInstructions$ = feature.getLoadedFeatures().pipe(map(features => {
        const all = features.reduce((allInstructions, currFeature) => {
          const definition = currFeature.getFeatureFunctionalityCached();
          const featurePluginInfo = {
            pluginName: currFeature.pluginName
          };
          const modelInstructionsList = definition.modelInstructions;
          if (modelInstructionsList?.length) {
            modelInstructionsList.forEach(instructionSet => {
              instructionSet.__pluginInstructionInfo = featurePluginInfo; // just for identification when debugging
              instructionSet.instructions.forEach(instr => {
                if (!instr.config.__pluginInstructionInfo) {
                  instr.config.__pluginInstructionInfo = featurePluginInfo;
                }
              });
            });
            allInstructions.push(...modelInstructionsList);
          }
          return allInstructions;
        }, []);
        return all;
      }), convertToSubject());
      debug.exposeDebugFunction(['Features', 'Field Instructions'], () => {
        return this.featureBasedInstructions$;
      });
    }
    getPluginInstructions(modelId, contextGroups = [], editorDefinition = 'default', editMode = null) {
      const currentContext = [this.platform, ...contextGroups];
      return this.getInstructionsForModelId(modelId).pipe(take(1), map(instructionInfos => {
        return this.filterApplicableModelInstructions(instructionInfos, currentContext, editorDefinition, editMode);
      }));
    }
    addPluginInstructionsToStaticInstructions(staticInstructions, pluginInstructionLists) {
      if (!staticInstructions) staticInstructions = [];
      const anyPluginInstructionUsesSlot = pluginInstructionLists.some(list => !!list.targetSlotId);
      if (!anyPluginInstructionUsesSlot) {
        return this.addPluginInstructionsToStaticInstructions_classic(staticInstructions, pluginInstructionLists);
      }
      const slotList = new Map();
      this.forEachInstructionInTree(staticInstructions, instr => {
        const layoutInstr = this.asLayoutInstruction(instr);
        const slotId = layoutInstr?.getSlotId() || null;
        if (slotId) {
          if (slotList.has(slotId)) throw new Error('Duplicate slotId detected in instructions list: "' + slotId + '"');
          slotList.set(slotId, layoutInstr);
          layoutInstr.resetPluginInstructions();
        }
      });
      // add pluginInstructionLists to either the targeted LayoutInstruction or rootInstructionList
      const rootInstructionLists = [];
      pluginInstructionLists.forEach(list => {
        if (list.targetSlotId) {
          const target = slotList.get(list.targetSlotId);
          if (!target) {
            if (!IS_PRODUCTION) console.warn('Unknown targetSlotId was passed: There is no layoutInstruction with slotId=' + list.targetSlotId + ' in this form!');
            rootInstructionLists.push(list);
          } else {
            target.addPluginInstructions(list.instructions);
          }
        } else {
          rootInstructionLists.push(list);
        }
      });
      return this.addPluginInstructionsToStaticInstructions_classic(staticInstructions, rootInstructionLists);
    }
    // as core does not know about the Instruction classes, this checks for existing methods so that we can work with it
    asLayoutInstruction(instr) {
      if (instr['addPluginInstructions'] && instr['getSlotId'] && instr['resetPluginInstructions']) return instr;else return null;
    }
    // classic merge strategy - pluginInstructions will simply be appended to end of list.
    addPluginInstructionsToStaticInstructions_classic(staticInstructions, pluginInstructionLists) {
      const pluginInstructions = this.mergeModelInstructionLists(pluginInstructionLists);
      const all = [...(staticInstructions || []), ...(pluginInstructions || [])];
      const preparedList = InstructionHelper.prepareInstructionsList(all);
      return preparedList.length === 0 ? null : preparedList;
    }
    mergeModelInstructionLists(instructionInfos) {
      return instructionInfos.reduce((allInstructions, info) => {
        return [...allInstructions, ...info.instructions];
      }, []);
    }
    getInstructionsForModelId(modelIdInput) {
      const id = getModelId(modelIdInput);
      return this.featureBasedInstructions$.pipe(map(allInstructions => {
        return allInstructions.filter(instruction => {
          return instruction.modelId === id;
        });
      }));
    }
    /** return a list of only those ModelInstructions that shall be used */
    filterApplicableModelInstructions(instructionInfos, currentContext, editorDefinition, editMode) {
      const filtered = instructionInfos.filter(info => {
        const contextMatches = info.displayContext ? matchContextGroups(currentContext, info.displayContext) : true;
        const editorDefinitionMatches = info.displayForEditorDefinitions ? info.displayForEditorDefinitions.includes(editorDefinition) : true;
        const editModeMatches = info.displayInEditModes && editMode ? info.displayInEditModes.includes(editMode) : true;
        const abilityCheckPassed = info.simpleAbilityCheck ? this.store.selectSnapshot(AccessState.simpleAbilityCheck(info.simpleAbilityCheck)) : true;
        // console.log('contextMatches, editorDefinitionMatches, editModeMatches',contextMatches, editorDefinitionMatches, editModeMatches)
        return contextMatches && editorDefinitionMatches && editModeMatches && abilityCheckPassed;
      });
      return filtered;
    }
    forEachInstructionInTree(tree, handler) {
      tree.forEach(instr => {
        handler(instr);
        // on core level, LayoutInstructions only exist as interfaces, so we cannot check against class.
        // instead we make a blind guess.
        if (instr.config['instructions'] && Array.isArray(instr.config['instructions'])) {
          this.forEachInstructionInTree(instr.config['instructions'], handler);
        }
      });
    }
    static {
      this.ɵfac = function ModelFieldInstructionService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ModelFieldInstructionService)(i0.ɵɵinject(i1.DevToolsRegistry), i0.ɵɵinject(i2.ConfigService), i0.ɵɵinject(i3.FeatureService), i0.ɵɵinject(i4.Store));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: ModelFieldInstructionService,
        factory: ModelFieldInstructionService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ModelFieldInstructionService;
})();